import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, computed, EventEmitter, inject, Input, input, NgZone, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { ExternalActionsService } from '@common/services/external-actions-service';
import { LanguageSettings } from '@common/services/language-settings';
import { Store } from '@ngrx/store';
import { PlaybookService } from '@playbook/services/playbook-service';
import { AppService } from '@src/suite/app.service';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { ServiceResponse } from '@src/suite/common/data-models/service/service-response';
import { ClipboardTypes } from '@src/suite/common/enums/clipboard-types';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { getExternalApiData } from '@src/suite/common/utils/record-utils';

@Component({
    selector: 'st-external-actions-overlay',
    templateUrl: './external-actions-overlay.html',
    styleUrls: ['./external-actions-overlay.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-external-actions-overlay',
    },
})
export class ExternalActionsOverlayComponent {
    ngZone = inject(NgZone);
    changeDetectorRef = inject(ChangeDetectorRef);
    languageSettings = inject(LanguageSettings);
    externalActionsService = inject(ExternalActionsService);
    appService = inject(AppService);
    playbookService = inject(PlaybookService);
    protected dialog = inject(MatDialog);
    modalService = inject(ModalService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    globalTablesService = inject(GlobalTablesService);
    private store = inject(Store);

    data = input<StorytellingTopicRecord | StorytellingAssignmentRecord | StorytellingStoryRecord>();
    editable = input<boolean>();
    disabled = input<boolean>();
    isNew = signal<boolean>(false);
    currentAction = signal<string>('');
    currentActionError = signal<boolean>(false);
    currentActionCompleted = signal<boolean>(false);
    @Input() public onObjectOpen: EventEmitter<any> = new EventEmitter();
    @Input() public onDelete: EventEmitter<any> = new EventEmitter();

    menuTrigger = viewChild(MatMenuTrigger);
    selectionList = viewChild(MatSelectionList);

    inFullScreen = input(false, {
        transform: coerceBooleanProperty,
    });

    inSidecarOpened = input(false, {
        transform: coerceBooleanProperty,
    });

    constructor() {}

    actions = computed(() => {
        if (this.currentApplication() === 'cast') {
            return this.externalActionsService.getExternalActionsForType(ObjectTypes.PUBLICATIONLIST);
        }
        return this.externalActionsService.getExternalActionsForV2(this.data());
    });

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    currentApplication = computed(() => {
        return this.store.selectSignal(selectCurrentApplication)();
    });

    displayOpenActionButtonLabel = computed(() => {
        const actionLabel = 'PSTWebButton';
        if (this.inSidecarOpened()) {
            return this.store
                .selectSignal(selectTranslatedKey(`${actionLabel}_${i18nKeys.PSTWebOpenText}`))()
                .includes(actionLabel)
                ? this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebOpenText))()
                : this.store.selectSignal(selectTranslatedKey(`${actionLabel}_${i18nKeys.PSTWebOpenText}`))();
        } else {
            return this.store
                .selectSignal(selectTranslatedKey(`${actionLabel}_${i18nKeys.PSTWebOpenFullScreenText}`))()
                .includes(actionLabel)
                ? this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebOpenFullScreenText))()
                : this.store.selectSignal(selectTranslatedKey(`${actionLabel}_${i18nKeys.PSTWebOpenFullScreenText}`))();
        }
    });

    displayOpenActionButtonHoverTitle = computed(() => {
        return this.globalTablesService.getDisplayOpenActionButtonHoverTitle(this.inSidecarOpened())();
    });

    getExternaActionsLabel(action: ExternalAction) {
        const actionLabel = 'PSTWebButton';
        return this.store
            .selectSignal(selectTranslatedKey(`${actionLabel}_${action.ID}`))()
            .includes(actionLabel)
            ? action.Description
            : this.store.selectSignal(selectTranslatedKey(`${actionLabel}__${action.ID}`))();
    }

    getExternaActionsHoverTitle(action: ExternalAction) {
        const actionLabel = 'PSTWebButtonHT';
        return this.store
            .selectSignal(selectTranslatedKey(`${actionLabel}_${action.ID}`))()
            .includes(actionLabel)
            ? action.Description
            : this.store.selectSignal(selectTranslatedKey(`${actionLabel}__${action.ID}`))();
    }

    openNewTemplateDialog() {
        // this.dialog.open(CreateNewTemplateComponent, {
        //     panelClass: 'st-create-new-template',
        //     width: '22rem',
        //     height: '22rem',
        //     hasBackdrop: true,
        //     data: this.data(),
        // });
        this.store.dispatch(RecordHttpActions.openNewTemplateDialog({ record: this.data() }));
    }

    copyToClipboard() {
        switch (this.data().Type) {
            case ObjectTypes.TOPIC:
                this.appService.clipboard.copy(this.data(), ClipboardTypes.Topic);
                break;
            case ObjectTypes.ASSIGNMENT:
                this.appService.clipboard.copy(this.data(), ClipboardTypes.Assignment);
                break;
            case ObjectTypes.SCRIPT:
                this.appService.clipboard.copy(this.data(), ClipboardTypes.Story);
                break;
        }
    }

    copyObjectUrlToClipboard() {
        this.appService.clipboard.copy(this.data(), ClipboardTypes.TopicUrl, 'playbook');
    }

    // async saveAsTemplate() {
    //     this.appService.navigationService.saveTemplateSettings(this.data());
    // }

    async cloneItem() {
        switch (this.data().Type) {
            case ObjectTypes.TOPIC:
                this.store.dispatch(RecordHttpActions.duplicateTopic({ linkToParent: true, oldRecord: this.data(), isNew: this.isNew() }));
                break;
            case ObjectTypes.ASSIGNMENT:
                this.store.dispatch(RecordHttpActions.duplicateAssignment({ record: this.data(), linkToParent: false }));
                break;
        }
    }

    async duplicateItem() {
        switch (this.data().Type) {
            case ObjectTypes.TOPIC:
                this.store.dispatch(RecordHttpActions.duplicateTopic({ linkToParent: true, oldRecord: this.data(), isNew: this.isNew() }));
                break;
            case ObjectTypes.ASSIGNMENT:
                this.store.dispatch(RecordHttpActions.duplicateAssignment({ record: this.data(), linkToParent: true }));
                break;
        }
    }

    actionIcon(action: ExternalAction) {
        if (action?.Icon && action?.Icon !== action?.ID) {
            return 'url(' + action.Icon + ')';
        } else {
            return null;
        }
    }

    actionChanged(event?: string) {
        const actionId: string = event || null;
        const action = this.actions().find(a => a.ID === actionId);
        if (action?.ID !== 'CLONE' && action?.ID !== 'DUPLICATE' && action?.ID !== 'DELETE' && action?.ID !== 'COPYLINK' && action?.ID !== 'TEMPLATE' && action?.ID !== 'COPYTOCLIPBOARD') {
            this.currentAction.set(action?.ID);
        }

        if (actionId !== null) {
            switch (actionId) {
                case 'DUPLICATE':
                    this.duplicateItem();
                    break;
                case 'DELETE':
                    this.deleteItem();
                    break;
                case 'CLONE':
                    this.cloneItem();
                    break;
                case 'COPYLINK':
                    this.copyObjectUrlToClipboard();
                    break;
                case 'TEMPLATE':
                    this.openNewTemplateDialog();
                    break;
                case 'COPYTOCLIPBOARD':
                    this.copyToClipboard();
                    break;
                default:
                    this.executeAction(action);
                    break;
            }
        }
    }

    getIcon(action): string {
        if (action.Icon !== null) {
            switch (action?.Icon) {
                case 'DUPLICATE':
                    return 'file_copy';
                case 'DELETE':
                    return 'delete';
                case 'CLONE':
                    return 'file_copy';
                case 'COPY LINK':
                    return 'link';
                case 'TEMPLATE':
                    return 'wysiwyg';
                case 'COPYTOCLIPBOARD':
                    return 'content_paste';
                default:
                    break;
            }
        }
    }

    async deleteItem() {
        this.store.dispatch(RecordHttpActions.deleteRecord({ record: this.data() }));
    }

    public async executeAction(action: ExternalAction) {
        this.currentActionError.set(false);
        this.currentActionCompleted.set(false);
        const res: ServiceResponse<ExternalActionResponse> = await this.externalActionsService.postExternalAction(getExternalApiData(action.ID, this.nomWebServicesJson.UserId, this.data()));
        if (!res) {
            this.currentActionError.set(true);
        } else {
            try {
                if (res.data && res.data.status === 200) {
                    this.currentActionCompleted.set(true);
                } else {
                    this.currentActionError.set(true);
                }
            } catch (e) {
                this.currentActionError.set(true);
                console.log(e);
            }
        }
    }

    onUpArrowPress() {
        if (this.selectionList().options.last) this.selectionList().options.last.focus();
    }

    onDownArrowPress() {
        if (this.selectionList().options.first) this.selectionList().options.first.focus();
    }

    onTabPress() {
        if (this.menuTrigger().menuOpen) this.menuTrigger().closeMenu();
    }
}
